/*
 * Copyright (c) 2019. Sonrai Analytics Ltd.
 * Developed by Ryan Duffy & Warren Dowey
 */

import { Injectable } from '@angular/core';
import { ProjectUsersModel } from '@app/models/project-users.model';
import { ApiBaseService } from '@app/services/api-services/api-base.service';
import { HttpClient } from '@angular/common/http';
import { ProjectUsersListService } from '@app/services/api-services/project-users/project-users-list.service';
import { ApiParamsModel } from '@app/models/api-params.model';
import { ApiFilterModel } from '@app/models/api-filter.model';
import { AuthUserService } from '@app/services/authentication/auth-user-service';
@Injectable({
  providedIn: 'root',
})
export class ProjectUserService extends ApiBaseService {
  private _projectUser: ProjectUsersModel;
  protected _endpoint = 'project_users';

  constructor(
    http: HttpClient,
    authUserService: AuthUserService,
    private projectUsersListService: ProjectUsersListService
  ) {
    super(http, authUserService);
  }

  get projectUser(): ProjectUsersModel {
    return this._projectUser;
  }

  set projectUser(value: ProjectUsersModel) {
    this._projectUser = value;
  }

  /**
   *  Warren Dowey & Ryan Duffy
   *
   *  getProjectFromListById
   *
   * : Get a ProjectUsersModel based on the id of the projectUsers within the model by checking the list
   *
   * @param id of type string
   *
   * @returns ProjectUsersModel: gets the projectUsers with the id that meets the query
   */
  getProjectUserFromListById(projectId: string): void {
    const projectUsers = this.projectUsersListService.getMultipleProjectUsersById(projectId);
    if (projectUsers.length) {
      this.dataSubject.next(projectUsers);
    } else {
      this.getProjectUserById(projectId);
      this.projectUsersListService.getData({});
    }
  }

  /**
   *  Ryan Duffy
   *
   *  getProjectFromList
   *
   *  Get ProjectUsersModel[] of all users
   *
   */
  getProjectUserFromList(): void {
    const projectUsers = this.projectUsersListService.projectUsersList;
    if (projectUsers?.length) {
      this.dataSubject.next(projectUsers);
    } else {
      this.getData({});
      this.projectUsersListService.getData({});
    }
  }
  /**
   *  Warren Dowey & Ryan Duffy
   *
   *  getProjectUserById
   *
   * : Get an projectUsers based on the id by filtering the API query
   *
   * @param id of type string
   *
   * @returns Void: gets the projectUsers with the id that meets the query
   */
  public getProjectUserById(projectId: string): void {
    const apiParamsModel = new ApiParamsModel();
    const apiFilterModel: ApiFilterModel = {
      field: 'project_id',
      operator: 'eq',
      value: projectId,
    };
    apiParamsModel.filter = [apiFilterModel];
    this.getData(apiParamsModel);
  }

  /**
   *  Warren Dowey & Ryan Duffy
   *
   *  getProjectUserById
   *
   * : Get an projectUsers based on the id by filtering the API query
   *
   * @param id of type string
   *
   * @returns Void: gets the projectUsers with the id that meets the query
   */
  public getProjectUserByIdAndUserId(projectId: string, userId: string): void {
    const apiParamsModel = new ApiParamsModel();
    const apiFilterModel: ApiFilterModel = {
      field: ['project_id', 'user_id'],
      operator: ['eq', 'eq'],
      value: [projectId, userId],
      joinOperator: 'AND',
    };
    apiParamsModel.filter = [apiFilterModel];
    this.getData(apiParamsModel);
  }
  /**
   *  Ryan Duffy
   *
   *  RemoveProjectUserByIdAndUserId
   *
   * : remove projectUsers based on the id by filtering the API query
   *
   * @param id of type string
   *
   * @returns Void: removes the projectUsers with the id that meets the query
   */
  public removeProjectUserByIdAndUserId(projectId: string, userId: string): void {
    const apiParamsModel = new ApiParamsModel();
    const apiFilterModel: ApiFilterModel = {
      field: ['project_id', 'user_id'],
      operator: ['eq', 'eq'],
      value: [projectId, userId],
      joinOperator: 'AND',
    };
    apiParamsModel.filter = [apiFilterModel];
    this.deleteData(apiParamsModel, {});
  }
  /**
   * Warren Dowey & Ryan Duffy
   *
   *  handleData
   *
   * : handles the data from the list by extending the handleData function from the API base service
   *
   * @param data of type ProjectUsersModel
   *
   * @returns Void: handle a get request from the postgres DB
   */
  protected handleData(data: ProjectUsersModel[]): void {
    this.projectUser = data[0];
    super.handleData(data);
  }
  /**
   *  Warren Dowey & Ryan Duffy
   *
   *  handleInsert
   *
   * : handles the insert of a new projectUsers into the list
   *
   * @param data of type ProjectUsersModel
   *
   * @returns Void: inserts a new projectUsers into the lsit
   */
  protected handleInsert(data: ProjectUsersModel[]): void {
    if (data) {
      this.projectUser = data[0];
      this.projectUsersListService.addToList(data[0]);
    }
    super.handleInsert(data);
  }
  /**
   *  Warren Dowey & Ryan Duffy
   *
   *  handleUpdate
   *
   * : handles the update of an projectUsers in the list
   *
   * @param data of type ProjectUsersModel
   *
   * @returns Void: update the lsit
   */
  protected handleUpdate(data: ProjectUsersModel): void {
    if (data) {
      this.projectUser = data[0];
      this.projectUsersListService.updateInList(data[0]);
    }
    super.handleUpdate(data);
  }
  /**
   *  Warren Dowey & Ryan Duffy
   *
   *  handleDelete
   *
   * : deletes an projectUsers from the list
   *
   * @param data of type ProjectUsersModel
   *
   * @returns Void: deletes a projectUsers from the list
   */
  protected handleDelete(data: ProjectUsersModel[]): void {
    super.handleDelete(data);
  }
}
